package io.jsonize.web.screens.editor

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.MutableState
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.AnnotatedString
import androidx.compose.ui.platform.LocalClipboardManager
import androidx.compose.ui.unit.dp
import io.jsonize.web.components.PreviewArea
import io.jsonize.web.components.SplitPane
import io.jsonize.web.components.showHistoryOverlay
import io.jsonize.web.navigation.NavController
import io.jsonize.web.services.FileExport
import io.jsonize.web.services.copyToClipboard
import io.jsonize.web.services.downloadTextFile
import io.jsonize.web.services.logButtonClick
import io.jsonize.web.view.CompareToolbar
import io.jsonize.web.view.CompareView
import io.jsonize.web.view.GeneratorPanel
import io.jsonize.web.view.GeneratorToolbar
import io.jsonize.web.view.JsonTreeView
import io.jsonize.web.viewmodel.AppViewModel
import kotlinx.coroutines.launch

@Composable
fun EditorScreen(
    nav: NavController,
    appViewModel: AppViewModel,
    editorPanelState: MutableState<EditorPanel>,
    lastContentPanelState: MutableState<EditorPanel>
) {
    val scope = rememberCoroutineScope()
    var activeTab by remember { mutableStateOf(FeatureTab.Tree) }
    var viewMode by remember { mutableStateOf(EditorViewMode.Code) }
    val parsedJson by appViewModel.parsedJson.collectAsState()
    val text by appViewModel.text.collectAsState()
    val compareText by appViewModel.compareText.collectAsState()
    val compareLeftDiffs by appViewModel.compareLeftDiffs.collectAsState()
    val clipboard = LocalClipboardManager.current
    val mainEditorRef = remember { mutableStateOf<EditorView?>(null) }

    BoxWithConstraints(Modifier.fillMaxSize()) {
        val isCompact = maxWidth < 900.dp

        LaunchedEffect(isCompact) {
            if (!isCompact) {
                viewMode = EditorViewMode.Code
            }
        }

        if (isCompact) {
            val activePanel = editorPanelState.value
            val contentPanel = if (activePanel == EditorPanel.History) {
                lastContentPanelState.value
            } else {
                activePanel
            }

            Box(Modifier.fillMaxSize()){
                EditorLeftPane(
                    appViewModel = appViewModel,
                    viewMode = viewMode,
                    onViewModeChange = { viewMode = it },
                    parsedJson = parsedJson,
                    showViewToggle = true,
                    lineDiffs = compareLeftDiffs,
                    externalEditorRef = mainEditorRef,
                    stackCompareLayout = true
                )
            }

            /*Box(Modifier.fillMaxSize()) {
                when (contentPanel) {
                    EditorPanel.Editor, EditorPanel.History -> EditorLeftPane(
                        appViewModel = appViewModel,
                        viewMode = viewMode,
                        onViewModeChange = { viewMode = it },
                        parsedJson = parsedJson,
                        showViewToggle = true,
                        lineDiffs = compareLeftDiffs,
                        externalEditorRef = mainEditorRef
                    )
                    EditorPanel.Preview -> PreviewArea(text)
                }

                EditorPanelSwitcher(
                    activePanel = activePanel,
                    onPanelSelected = { panel ->
                        editorPanelState.value = panel
                        if (panel == EditorPanel.History) {
                            showHistoryOverlay()
                        } else {
                            lastContentPanelState.value = panel
                        }
                    },
                    modifier = Modifier
                        .align(Alignment.BottomCenter)
                        .padding(bottom = 16.dp)
                )
            }*/
        } else {
            SplitPane(
                left = {
                    EditorLeftPane(
                        appViewModel = appViewModel,
                        viewMode = viewMode,
                        onViewModeChange = { viewMode = it },
                        parsedJson = parsedJson,
                        showViewToggle = false,
                        lineDiffs = compareLeftDiffs,
                        externalEditorRef = mainEditorRef
                    )
                },
                right = {
                    Row(Modifier.fillMaxSize()) {
                        Column(modifier = Modifier.weight(1f).fillMaxHeight()) {
                            when (activeTab) {
                                FeatureTab.Tree -> JsonTreeView(parsedJson)
                                FeatureTab.Compare -> CompareView(appViewModel, mainEditorRef)
                                FeatureTab.Generator -> GeneratorPanel(appViewModel, text)
                            }
                        }
                        FeatureToolbar(
                            "", false,
                            treeView = { activeTab = FeatureTab.Tree },
                            compareView = { activeTab = FeatureTab.Compare },
                            generatorView = { activeTab = FeatureTab.Generator },
                            viewMode = viewMode,
                            onViewModeChange = { },
                            showViewToggle = false
                        ){
                            when(activeTab){
                                FeatureTab.Tree -> {}
                                FeatureTab.Compare -> { CompareToolbar(
                                    compareText,
                                    format = {
                                        appViewModel.formatCompareJsonAsync()
                                    },
                                    minify = {
                                        appViewModel.minifyCompareJsonAsync()
                                    },
                                    clear = {
                                        appViewModel.updateCompareText("")
                                        appViewModel.clearCompareDiffs()
                                    },
                                    swap = {
                                        appViewModel.swapCompareTexts()
                                    },
                                    copy = {
                                        clipboard.setText(AnnotatedString(appViewModel.compareText.value))
                                    },
                                    download = {
                                        FileExport.default.downloadJson("jsonize.json",
                                            appViewModel.compareText.value)
                                    }
                                ) }
                                FeatureTab.Generator -> {
                                    GeneratorToolbar(
                                        appViewModel = appViewModel,
                                        copy = {
                                            scope.launch {
                                                copyToClipboard(appViewModel.code.value)
                                            }
                                        },
                                        download = {
                                            scope.launch {
                                                downloadTextFile(appViewModel.codeFileName.value, appViewModel.code.value)
                                            }
                                        }
                                    )
                                }
                            }
                        }
                    }
                    /*Column(modifier = Modifier.fillMaxSize().padding(8.dp)) {
                        Row(
                            horizontalArrangement = Arrangement.spacedBy(8.dp),
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            TabButton(
                                label = "Tree",
                                active = activeTab == FeatureTab.Tree,
                                onClick = { activeTab = FeatureTab.Tree }
                            )
                            TabButton(
                                label = "Compare",
                                active = activeTab == FeatureTab.Compare,
                                onClick = { activeTab = FeatureTab.Compare }
                            )
                            TabButton(
                                label = "Generator",
                                active = activeTab == FeatureTab.Generator,
                                onClick = { activeTab = FeatureTab.Generator }
                            )
                        }

                        Spacer(Modifier.height(8.dp))


                    }*/
                }
            )
        }
    }
}

enum class FeatureTab { Tree, Compare, Generator }

@Composable
private fun TabButton(label: String, active: Boolean, onClick: () -> Unit) {
    Button(
        onClick = {
            logButtonClick(label)
            onClick()
        },
        colors = if (active)
            ButtonDefaults.buttonColors()
        else
            ButtonDefaults.outlinedButtonColors()
    ) {
        Text(label)
    }
}

@Composable
private fun EditorPanelSwitcher(
    activePanel: EditorPanel,
    onPanelSelected: (EditorPanel) -> Unit,
    modifier: Modifier = Modifier
) {
    val items = listOf(
        EditorPanel.Editor to "Editor",
        EditorPanel.History to "History",
        EditorPanel.Preview to "Preview"
    )

    Surface(
        modifier = modifier,
        shape = MaterialTheme.shapes.extraLarge,
        tonalElevation = 4.dp,
        shadowElevation = 8.dp
    ) {
        Row(
            modifier = Modifier.padding(horizontal = 8.dp, vertical = 6.dp),
            horizontalArrangement = Arrangement.spacedBy(6.dp)
        ) {
            items.forEach { (panel, label) ->
                val selected = panel == activePanel
                TextButton(
                    onClick = {
                        logButtonClick(label)
                        onPanelSelected(panel)
                    },
                    colors = ButtonDefaults.textButtonColors(
                        containerColor = if (selected) {
                            MaterialTheme.colorScheme.secondaryContainer
                        } else {
                            Color.Transparent
                        },
                        contentColor = if (selected) {
                            MaterialTheme.colorScheme.onSecondaryContainer
                        } else {
                            MaterialTheme.colorScheme.onSurfaceVariant
                        }
                    ),
                    shape = MaterialTheme.shapes.large,
                    contentPadding = PaddingValues(horizontal = 12.dp, vertical = 6.dp),
                    modifier = Modifier.heightIn(min = 32.dp)
                ) {
                    Text(label, style = MaterialTheme.typography.labelLarge)
                }
            }
        }
    }
}
